import React, { useState, useEffect } from "react";
import CREDENTIAL_TYPES from "./credentialTypes";
import {
  Button,
  Modal,
  Select,
  TextInput,
  PasswordInput,
  NumberInput,
  Checkbox,
  Stack,
  Group,
  Title,
  Table,
  ActionIcon,
  Text,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import { useServerApi } from "../../hooks/userServerApi";
import _ from "lodash";
import { IconTrash, IconEdit } from "@tabler/icons";

const WorkflowCredentials = ({ form: mainForm }) => {
  const [opened, setOpened] = useState(false);
  const [editOpened, setEditOpened] = useState(false);
  const [selectedType, setSelectedType] = useState("");
  const [api] = useServerApi();
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingCredential, setEditingCredential] = useState(null);
  const workflowId = _.get(mainForm.values, "_id");

  const form = useForm({
    initialValues: {
      name: "",
      credentialType: "",
      credentialFields: [],
    },
    validate: {
      name: (value) => (!value ? "Name is required" : null),
      credentialType: (value) =>
        !value ? "Please select a credential type" : null,
    },
  });

  const editForm = useForm({
    initialValues: {
      name: "",
      credentialType: "",
      credentialFields: [],
    },
    validate: {
      name: (value) => (!value ? "Name is required" : null),
      credentialType: (value) =>
        !value ? "Please select a credential type" : null,
    },
  });

  const fetchCredentials = async () => {
    if (!workflowId) return;

    try {
      setLoading(true);
      const credentials = await api.AIWorkFlow.getCredentials(workflowId);
      console.log("credentials", credentials);
      setCredentials(credentials);
    } catch (error) {
      showNotification({
        title: "Error",
        message: "Failed to fetch credentials",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCredentials();
  }, [workflowId]);

  const handleTypeChange = (value) => {
    setSelectedType(value);
    form.reset();
    form.setFieldValue("credentialType", value);

    // Set default values for the selected credential type
    const selectedCredentialType = CREDENTIAL_TYPES.find(
      (type) => type.type === value
    );
    if (selectedCredentialType) {
      const credentialFields = selectedCredentialType.fields.map((field) => ({
        name: field.name,
        value:
          field.defaultValue !== undefined ? field.defaultValue.toString() : "",
      }));
      form.setFieldValue("credentialFields", credentialFields);
    }
  };

  const handleEditTypeChange = (value) => {
    editForm.setFieldValue("credentialType", value);

    // Set default values for the selected credential type
    const selectedCredentialType = CREDENTIAL_TYPES.find(
      (type) => type.type === value
    );
    if (selectedCredentialType) {
      const credentialFields = selectedCredentialType.fields.map((field) => ({
        name: field.name,
        value:
          field.defaultValue !== undefined ? field.defaultValue.toString() : "",
      }));
      editForm.setFieldValue("credentialFields", credentialFields);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    const credentialFields = form.values.credentialFields.map((field) => {
      if (field.name === fieldName) {
        return {
          ...field,
          value: value !== null && value !== undefined ? value.toString() : "",
        };
      }
      return field;
    });
    form.setFieldValue("credentialFields", credentialFields);
  };

  const handleEditFieldChange = (fieldName, value) => {
    const credentialFields = editForm.values.credentialFields.map((field) => {
      if (field.name === fieldName) {
        return {
          ...field,
          value: value !== null && value !== undefined ? value.toString() : "",
        };
      }
      return field;
    });
    editForm.setFieldValue("credentialFields", credentialFields);
  };

  const handleSubmit = async (values) => {
    try {
      console.log("Submitting values:", values); // Debug log
      const payload = {
        name: values.name,
        credentialType: selectedType, // Use selectedType instead of values.credentialType
        workflowId,
        credentialFields: values.credentialFields,
      };
      console.log("Submitting payload:", payload); // Debug log

      const response = await api.AIWorkFlow.addCredential(payload);
      console.log("response > ", response);

      showNotification({
        title: "Success",
        message: "Credential saved successfully",
        color: "green",
      });

      setOpened(false);
      form.reset();
      setSelectedType("");
      fetchCredentials();
    } catch (error) {
      console.error("Error saving credential:", error); // Debug log
      showNotification({
        title: "Error",
        message: error.message || "Failed to save credential",
        color: "red",
      });
    }
  };

  const handleEdit = (credential) => {
    // Find the credential type based on the fields
    const credentialType = CREDENTIAL_TYPES.find((type) =>
      type.fields.every((field) =>
        credential.credentialFields.some((cf) => cf.name === field.name)
      )
    );

    setEditingCredential(credential);
    editForm.setValues({
      name: credential.name,
      credentialType: credential.credentialType || "",
      credentialFields: credential.credentialFields,
    });
    setEditOpened(true);
  };

  const handleEditSubmit = async (values) => {
    try {
      const payload = {
        _id: editingCredential._id,
        name: values.name,
        credentialType: values.credentialType,
        workflowId,
        credentialFields: values.credentialFields,
      };

      await api.AIWorkFlow.updateCredential(editingCredential._id, payload);

      showNotification({
        title: "Success",
        message: "Credential updated successfully",
        color: "green",
      });

      setEditOpened(false);
      setEditingCredential(null);
      editForm.reset();
      fetchCredentials();
    } catch (error) {
      showNotification({
        title: "Error",
        message: error.message || "Failed to update credential",
        color: "red",
      });
    }
  };

  const handleDelete = async (credentialId) => {
    try {
      const confirmed = window.confirm(
        "Are you sure you want to delete this credential?"
      );
      if (!confirmed) return;
      await api.AIWorkFlow.deleteCredential(credentialId);
      showNotification({
        title: "Success",
        message: "Credential deleted successfully",
        color: "green",
      });
      fetchCredentials();
    } catch (error) {
      showNotification({
        title: "Error",
        message: "Failed to delete credential",
        color: "red",
      });
    }
  };

  const selectedCredentialType = CREDENTIAL_TYPES.find(
    (type) => type.type === selectedType
  );

  const renderCredentialFields = (form, handleFieldChange) => {
    const selectedType = form.values.credentialType;
    const selectedCredentialType = CREDENTIAL_TYPES.find(
      (type) => type.type === selectedType
    );

    if (!selectedCredentialType) return null;

    return selectedCredentialType.fields.map((field) => {
      const fieldValue =
        form.values.credentialFields.find((f) => f.name === field.name)
          ?.value || "";

      switch (field.type) {
        case "password":
          return (
            <PasswordInput
              key={field.name}
              label={field.label}
              placeholder={field.placeholder}
              value={fieldValue}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              required
              autoComplete="new-password"
            />
          );
        case "number":
          return (
            <NumberInput
              key={field.name}
              label={field.label}
              placeholder={field.placeholder}
              value={fieldValue ? Number(fieldValue) : ""}
              onChange={(value) => handleFieldChange(field.name, value)}
              required
              autoComplete="off"
              precision={field.precision ?? 2}
            />
          );
        case "checkbox":
          return (
            <Checkbox
              key={field.name}
              label={field.label}
              checked={fieldValue === "true"}
              onChange={(e) => handleFieldChange(field.name, e.target.checked)}
            />
          );
        default:
          return (
            <TextInput
              key={field.name}
              label={field.label}
              placeholder={field.placeholder}
              value={fieldValue}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              required
              autoComplete="off"
            />
          );
      }
    });
  };

  return (
    <div>
      <Group position="right" mb="xl">
        {/* <Title order={3}>Credentials</Title> */}
        <Button onClick={() => setOpened(true)} size="xs">
          Add Credential
        </Button>
      </Group>

      <Table striped highlightOnHover>
        <thead>
          <tr>
            <th>Name</th>
            <th>Type</th>
            <th style={{ width: 100 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {credentials.length === 0 ? (
            <tr>
              <td colSpan={3}>
                <Text align="center" color="dimmed">
                  No credentials found
                </Text>
              </td>
            </tr>
          ) : (
            credentials.map((credential) => (
              <tr key={credential._id}>
                <td>{credential.name}</td>
                <td>{credential.credentialType}</td>
                <td>
                  <Group spacing={4} position="center">
                    <ActionIcon
                      size="sm"
                      onClick={() => handleEdit(credential)}
                    >
                      <IconEdit size={16} />
                    </ActionIcon>
                    <ActionIcon
                      size="sm"
                      onClick={() => handleDelete(credential._id)}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {/* Add Credential Modal */}
      <Modal
        opened={opened}
        onClose={() => {
          setOpened(false);
          form.reset();
          setSelectedType("");
        }}
        title="Add New Credential"
        size="lg"
        closeOnClickOutside={false}
      >
        <form onSubmit={form.onSubmit(handleSubmit)} autoComplete="off">
          <Stack spacing="md">
            <Select
              label="Credential Type"
              placeholder="Select a type"
              data={CREDENTIAL_TYPES.map((type) => ({
                value: type.type,
                label: type.label,
              }))}
              value={selectedType}
              onChange={handleTypeChange}
              required
              autoComplete="off"
            />
            <TextInput
              label="Credential Name"
              placeholder="Enter credential name"
              {...form.getInputProps("name")}
              required
              autoComplete="off"
            />

            {renderCredentialFields(form, handleFieldChange)}

            <Group position="right" mt="md">
              <Button
                variant="default"
                onClick={() => setOpened(false)}
                size="xs"
              >
                Cancel
              </Button>
              <Button type="submit" size="xs">
                Save Credential
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* Edit Credential Modal */}
      <Modal
        opened={editOpened}
        onClose={() => {
          setEditOpened(false);
          setEditingCredential(null);
          editForm.reset();
        }}
        title="Edit Credential"
        size="lg"
        closeOnClickOutside={false}
      >
        <form onSubmit={editForm.onSubmit(handleEditSubmit)} autoComplete="off">
          <Stack spacing="md">
            <Select
              label="Credential Type"
              placeholder="Select a type"
              data={CREDENTIAL_TYPES.map((type) => ({
                value: type.type,
                label: type.label,
              }))}
              value={editForm.values.credentialType}
              onChange={handleEditTypeChange}
              required
              autoComplete="off"
            />
            <TextInput
              label="Credential Name"
              placeholder="Enter credential name"
              {...editForm.getInputProps("name")}
              required
              autoComplete="off"
            />

            {renderCredentialFields(editForm, handleEditFieldChange)}

            <Group position="right" mt="md">
              <Button
                variant="default"
                onClick={() => {
                  setEditOpened(false);
                  setEditingCredential(null);
                  editForm.reset();
                }}
                size="xs"
              >
                Cancel
              </Button>
              <Button type="submit" size="xs">
                Update Credential
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </div>
  );
};

export default WorkflowCredentials;
